import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAutoReplyMessageAsset,
  deleteAutoReplyMessageAsset,
  getAssetUpdateConfirmation,
  getAutoReplyMessageAssets,
  TAssetUpdateImpact,
  TAutoReplyMessageAsset,
  TCardTypeMessagePayload,
  updateAutoReplyMessageAsset,
} from 'api';
import { ConfirmModal } from 'components/ConfirmModal';
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context';
import { getQueryOrganizationId } from 'configs/queryClient';
import { Snackbar } from 'components/Snackbar';
import { pageCardClasses } from 'components/AppLayout/styles';
import { autoReplyMessageStrings } from 'constants/strings';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import {
  cardToLegacyFields,
  normalizeCardTypePayload,
} from 'utils/cardTypeMessage';
import { issueMessage } from 'utils/scenarioIssues';
import { cn } from 'utils/cn';
import { CardTypeMessageEditor } from './CardTypeMessageEditor';

type SaveVariables = {
  targetId: number | null;
  payload: TCardTypeMessagePayload;
  name: string;
  expectedImpact?: TAssetUpdateImpact;
};

export default function CardTypeMessagePage() {
  const queryClient = useQueryClient();
  // 組織が変わると画面ごと作り直されるため、この画面の操作は常に表示中の組織へ送る
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const assetQueryKey = [
    queryKeys.AUTO_REPLY_MESSAGE_ASSETS,
    'CARD_TYPE_MESSAGE',
  ] as const;
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [savedRevision, setSavedRevision] = useState(0);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [mutationLocked, setMutationLocked] = useState(false);
  const mutationInFlightRef = useRef(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    variables: SaveVariables;
    impact: TAssetUpdateImpact;
  }>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({
    open: false,
    message: '',
    variant: 'success',
  });

  const strings = lineStrings.management.cardTypeMessage;

  const { data: assets = [] } = useQuery({
    queryKey: assetQueryKey,
    queryFn: () => getAutoReplyMessageAssets('CARD_TYPE_MESSAGE'),
  });

  const selectedAsset = assets.find((a) => a.id === editingId);
  const parsedInitial = selectedAsset
    ? normalizeCardTypePayload(selectedAsset.payload)
    : null;

  const releaseMutationLock = () => {
    mutationInFlightRef.current = false;
    setMutationLocked(false);
  };

  const tryStartMutation = (): boolean => {
    if (isUploadingImage || mutationInFlightRef.current) return false;
    mutationInFlightRef.current = true;
    setMutationLocked(true);
    return true;
  };

  const resetEditorState = () => {
    setEditingId(null);
    setEditorKey((k) => k + 1);
  };

  const saveMutation = useMutation({
    mutationFn: async ({
      targetId,
      payload,
      name,
      expectedImpact,
    }: SaveVariables) => {
      const firstCard = payload.cards[0];
      const legacy = firstCard ? cardToLegacyFields(firstCard) : null;
      const body = {
        type: 'CARD_TYPE_MESSAGE' as const,
        name,
        payload,
        previewImageUrl: legacy?.imageUrl ?? firstCard?.imageUrl ?? null,
        ...(expectedImpact ? { expectedImpact } : {}),
      };
      return targetId !== null
        ? updateAutoReplyMessageAsset(targetId, body, organizationId)
        : createAutoReplyMessageAsset(body, organizationId);
    },
    // 戻る/進むで組織が変わった後に解決しても、別組織のキャッシュへ書かない
    onMutate: () => ({ queryOrganizationId: getQueryOrganizationId() }),
    onSuccess: async (savedAsset, variables, context) => {
      setPendingUpdate(undefined);
      const isSameOrganization =
        context?.queryOrganizationId === getQueryOrganizationId();
      if (isSameOrganization) {
        queryClient.setQueryData<TAutoReplyMessageAsset[]>(
          assetQueryKey,
          (current = []) =>
            current.some((asset) => asset.id === savedAsset.id)
              ? current.map((asset) =>
                  asset.id === savedAsset.id ? savedAsset : asset,
                )
              : [savedAsset, ...current],
        );
      }
      if (variables.targetId === null) {
        setEditingId(savedAsset.id);
      }
      setSavedRevision((revision) => revision + 1);
      if (isSameOrganization) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
        });
      }
      setSnackbar({
        open: true,
        message: autoReplyMessageStrings.assets.saveSuccess,
        variant: 'success',
      });
    },
    onError: (error: Error, variables) => {
      const confirmation = getAssetUpdateConfirmation(error);
      if (confirmation) {
        setPendingUpdate({ variables, impact: confirmation.impact });
        return;
      }
      setSnackbar({
        open: true,
        message: issueMessage(error, strings.errors.saveFailed),
        variant: 'error',
      });
    },
    onSettled: (_data, error) => {
      if (!getAssetUpdateConfirmation(error)) releaseMutationLock();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (targetId: number) =>
      deleteAutoReplyMessageAsset(targetId, organizationId),
    onMutate: () => ({ queryOrganizationId: getQueryOrganizationId() }),
    onSuccess: async (_result, targetId, context) => {
      if (context?.queryOrganizationId === getQueryOrganizationId()) {
        queryClient.setQueryData<TAutoReplyMessageAsset[]>(
          assetQueryKey,
          (current = []) => current.filter((asset) => asset.id !== targetId),
        );
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
        });
      }
      resetEditorState();
      setSnackbar({
        open: true,
        message: autoReplyMessageStrings.assets.deleteSuccess,
        variant: 'success',
      });
    },
    onError: (error: Error) =>
      setSnackbar({
        open: true,
        message: issueMessage(error, strings.errors.deleteFailed),
        variant: 'error',
      }),
    onSettled: releaseMutationLock,
  });

  const isBusy =
    isUploadingImage ||
    mutationLocked ||
    saveMutation.isPending ||
    deleteMutation.isPending;

  const resetEditor = () => {
    if (isBusy || mutationInFlightRef.current) return;
    resetEditorState();
  };

  const selectAsset = (assetId: number) => {
    if (isBusy || mutationInFlightRef.current) return;
    setEditingId(assetId);
    setEditorKey((k) => k + 1);
  };

  const save = (payload: TCardTypeMessagePayload, name: string) => {
    if (!tryStartMutation()) return;
    saveMutation.mutate({ targetId: editingId, payload, name });
  };

  const remove = () => {
    if (editingId === null || !tryStartMutation()) return;
    deleteMutation.mutate(editingId);
  };

  return (
    <>
      <section
        data-testid="card-type-page-layout"
        className={cn(
          pageCardClasses.regular,
          'grid min-h-[calc(100vh-12rem)] gap-6 sm:p-6 xl:grid-cols-[300px_minmax(0,1fr)]',
        )}
      >
        <aside className="rounded-2xl border border-border/70 bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-ink">
                {strings.createTitle}
              </h1>
              <p className="mt-1 text-xs text-muted">
                {strings.createDescription}
              </p>
            </div>
            <button
              type="button"
              disabled={isBusy}
              onClick={resetEditor}
              className="shrink-0 whitespace-nowrap text-xs font-semibold text-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {strings.listNew}
            </button>
          </div>
          <div className="grid gap-2">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                disabled={isBusy}
                onClick={() => selectAsset(asset.id)}
                className={`rounded-xl border p-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                  editingId === asset.id
                    ? 'border-accent bg-accent-soft'
                    : 'border-border'
                }`}
              >
                <span className="block font-semibold">{asset.name}</span>
                <span className="text-xs text-muted">
                  {normalizeCardTypePayload(asset.payload)?.templateId ?? '—'} ·{' '}
                  {(asset.payload as TCardTypeMessagePayload).cards?.length ??
                    0}{' '}
                  cards
                </span>
              </button>
            ))}
          </div>
        </aside>

        <div className="min-w-0 overflow-hidden rounded-2xl border border-border/70 bg-white p-5">
          <CardTypeMessageEditor
            key={editorKey}
            initial={
              parsedInitial
                ? {
                    itemId: editingId,
                    name: selectedAsset?.name,
                    altText: parsedInitial.altText,
                    templateId: parsedInitial.templateId,
                    cards: parsedInitial.cards,
                  }
                : undefined
            }
            isSaving={saveMutation.isPending}
            isDeleting={deleteMutation.isPending}
            isBusy={isBusy}
            canDelete={editingId !== null}
            onDelete={remove}
            onUploadError={(message) =>
              setSnackbar({
                open: true,
                message,
                variant: 'error',
              })
            }
            onUploadPendingChange={setIsUploadingImage}
            onSave={save}
            savedRevision={savedRevision}
          />
        </div>
      </section>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        variant={snackbar.variant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
      <ConfirmModal
        open={!!pendingUpdate}
        textTitle="この素材を使う配信内容が変わります"
        textMessage={
          pendingUpdate
            ? `シナリオ ${pendingUpdate.impact.scenarioCount}件、未完了Run ${pendingUpdate.impact.openRunCount}件に反映されます。保存しますか？`
            : undefined
        }
        textCancel="キャンセル"
        textConfirm="確認して保存"
        isLoading={saveMutation.isPending}
        onClose={() => {
          setPendingUpdate(undefined);
          releaseMutationLock();
        }}
        onConfirm={() => {
          if (!pendingUpdate) return;
          saveMutation.mutate({
            ...pendingUpdate.variables,
            expectedImpact: pendingUpdate.impact,
          });
        }}
      />
    </>
  );
}
